import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Paper, Stack } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import PageHeader from "../../../../components/common/PageHeader";
import ActionIconButton from "../../../../components/common/ActionIconButton";
import PageLoader from "../../../../components/common/PageLoader";
import ChipList from "../../../../components/common/ChipList";
import TruncatedText from "../../../../components/common/TruncatedText";
import EmptyState from "../../../../components/common/EmptyState";
import SearchFilterBar from "../../../../components/common/SearchFilterBar";
import DataTable from "../../../../components/DataTable";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import { tenantUserProfileService } from "../../services";
import useAuth from "../../../../hooks/useAuth";
import {
  formatMfaStatus,
  formatPasswordExpiryDate,
  formatPasswordExpiryDays,
  matchesUserProfileSearch,
  normalizeUserProfile,
  normalizeUserProfileList,
  sortUserProfiles,
} from "../../utils/normalizeUserProfile";
import UserProfileFormDialog from "../UserProfileFormDialog";

const UserProfileManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTenantId, tenantId } = useAuth();
  const requestTenantId = activeTenantId || tenantId;
  const listBase = location.pathname.startsWith("/tenant-")
    ? "/tenant-user-profiles"
    : "/user-profiles";

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [appliedName, setAppliedName] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const pendingCreatedRef = React.useRef(null);

  const loadProfiles = useCallback(
    async (name = appliedName, savedProfile = null) => {
      setLoading(true);
      setError("");
      try {
        const payload = await tenantUserProfileService.getAllTenantUserProfiles(
          requestTenantId
        );
        const query = String(name || "").trim();
        const pending = savedProfile || pendingCreatedRef.current;
        let normalized = sortUserProfiles(
          normalizeUserProfileList(payload).filter((item) =>
            matchesUserProfileSearch(item, { name: query })
          )
        );

        if (
          pending?.profileName &&
          !normalized.some(
            (item) =>
              String(item.profileName).toLowerCase() ===
              String(pending.profileName).toLowerCase()
          ) &&
          matchesUserProfileSearch(pending, { name: query })
        ) {
          normalized = sortUserProfiles([
            normalizeUserProfile(pending),
            ...normalized,
          ]);
        } else if (pending) {
          const foundInApi = normalized.some(
            (item) =>
              String(item.profileName).toLowerCase() ===
              String(pending.profileName).toLowerCase()
          );
          if (foundInApi) {
            pendingCreatedRef.current = null;
          }
        }

        setProfiles(normalized);
        setError("");
      } catch (err) {
        const message = getApiErrorMessage(err);
        setProfiles([]);
        setError(message);
        dispatch(appActions.apiErrorMessage(message));
      } finally {
        setLoading(false);
      }
    },
    [appliedName, dispatch, requestTenantId]
  );

  useEffect(() => {
    loadProfiles(appliedName);
  }, [appliedName, loadProfiles]);

  const openDetails = useCallback(
    (row) => {
      const id = row?.profileName;
      if (!id) return;
      navigate(`${listBase}/view/${encodeURIComponent(id)}`, {
        state: { profile: row },
      });
    },
    [listBase, navigate]
  );

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      const profileName = String(payload.profileName || "").trim();
      const created = await tenantUserProfileService.createTenantUserProfile(
        {
          profileName,
          passwordExpiryInSecs: payload.passwordExpiryInSecs,
        },
        requestTenantId
      );

      const hasMfa =
        payload.mfaConfiguration &&
        (payload.mfaConfiguration.enforcement ||
          (Array.isArray(payload.mfaConfiguration.methods) &&
            payload.mfaConfiguration.methods.length));

      if (hasMfa) {
        await tenantUserProfileService.updateTenantUserProfile(
          profileName,
          {
            passwordExpiryInSecs: payload.passwordExpiryInSecs,
            mfaConfiguration: payload.mfaConfiguration,
          },
          requestTenantId
        );
      }

      const savedProfile = normalizeUserProfile({
        profileName,
        passwordExpiryInSecs: payload.passwordExpiryInSecs,
        mfaConfiguration: payload.mfaConfiguration,
        ...(created && typeof created === "object" ? created : {}),
        updatedAt: new Date().toISOString(),
      });
      pendingCreatedRef.current = savedProfile;
      dispatch(
        appActions.apiSuccessMessage("User profile created successfully.")
      );
      setFormOpen(false);
      setNameFilter("");
      setAppliedName("");
      await loadProfiles("", savedProfile);
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "profileName",
        headerName: "Profile Name",
        flex: 1.2,
        minWidth: 180,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "passwordExpiryInSecs",
        headerName: "Password Expiry",
        flex: 1,
        minWidth: 160,
        valueGetter: (value, row) => {
          const secs = row?.passwordExpiryInSecs;
          return formatPasswordExpiryDate(secs, row?.passwordExpiryAt);
        },
        renderCell: (params) => {
          const row = params.row;
          const secs = row?.passwordExpiryInSecs;
          const dateLabel = formatPasswordExpiryDate(
            secs,
            row?.passwordExpiryAt
          );
          const daysLabel = formatPasswordExpiryDays(secs);
          const tooltip =
            row?.passwordExpiryAt != null
              ? dateLabel
              : daysLabel
                ? `Max password age: ${daysLabel}. Date shown if the password were set today.`
                : dateLabel;
          return (
            <TruncatedText value={dateLabel} tooltip={tooltip} />
          );
        },
      },
      {
        field: "mfaStatus",
        headerName: "MFA",
        flex: 0.7,
        minWidth: 110,
        valueGetter: (value, row) => formatMfaStatus(row),
        renderCell: (params) => (
          <TruncatedText value={formatMfaStatus(params.row)} />
        ),
      },
      {
        field: "mfaMethods",
        headerName: "MFA Methods",
        flex: 1.2,
        minWidth: 180,
        sortable: false,
        valueGetter: (value, row) =>
          Array.isArray(row?.mfaConfiguration?.methods)
            ? row.mfaConfiguration.methods.join(", ")
            : "",
        renderCell: (params) => (
          <ChipList
            items={params.row?.mfaConfiguration?.methods || []}
            maxVisible={2}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <ActionIconButton
            title="View Details"
            icon={<VisibilityOutlinedIcon fontSize="small" />}
            onClick={() => openDetails(params.row)}
          />
        ),
      },
    ],
    [openDetails]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ mb: 1 }}
      >
        <PageHeader
          title="User Profiles"
          subtitle="Manage tenant user profile types and MFA configuration."
          sx={{ mb: 0 }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => setFormOpen(true)}
          >
            Add Profile
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => loadProfiles(appliedName)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <SearchFilterBar
        fields={[
          {
            key: "name",
            label: "Profile Name",
            value: nameFilter,
            onChange: (e) => setNameFilter(e.target.value),
            width: 280,
          },
        ]}
        onSearch={() => setAppliedName(nameFilter.trim())}
        onClear={() => {
          setNameFilter("");
          setAppliedName("");
        }}
      />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          minHeight: 360,
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading && !profiles.length ? (
          <PageLoader text="Loading user profiles..." />
        ) : !loading && !profiles.length ? (
          <EmptyState
            title="No user profiles found"
            description={
              appliedName
                ? "No profile matched that name. Try another search or create a profile."
                : "Create a user profile or search by exact profile name."
            }
          />
        ) : (
          <DataTable
            rows={profiles}
            columns={columns}
            pageSize={20}
            showPagination
            loading={loading}
            autoHeight
            getRowId={(row) => row.profileName || row.id}
            paginationMode="client"
          />
        )}
      </Paper>

      <UserProfileFormDialog
        open={formOpen}
        mode="create"
        profile={null}
        loading={saving}
        onClose={() => setFormOpen(false)}
        onSubmit={handleCreate}
      />
    </Box>
  );
};

export default UserProfileManagement;
